import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Controller, FieldValues, Path } from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon,
  Select,
  Switch,
  Textfield
} from '@clientbase/clientbase-library'
import { Box, MenuItem, Stack, Tooltip, Typography } from '@mui/material'
import { SelectRowTable } from 'hooks/use-select-row-table'
import moment from 'moment'

import { DatePicker } from 'components/DatePicker'
import NumberFormatCustom from 'components/PorcentageInput/PorcentageInput'
import { Table } from 'components/Table'

import { formatMoney } from 'utils/FormatMoney'
import { FrequencyUnit, monthsPerCycle } from 'utils/frequency'

import { theme } from 'styles/theme'

import { AdjustmentAccordionProps } from './AdjustmentAccordion.interface'

// Adjustment type options
export const adjustmentTypeOptions = [
  { label: 'Personalizado', value: 'customized' },
  { label: 'IPCA', value: 'ipca' },
  { label: 'IGPM', value: 'igpm' }
]

// Frequency options with months value
export const adjustmentFrequencyOptions = [
  { label: 'Trimestral', value: 'quarterly', months: 3 },
  { label: 'Semestral', value: 'semester', months: 6 },
  { label: 'Anual', value: 'annual', months: 12 }
]

// Recurrence frequency to months mapping
// 0=weekly, 1=monthly, 2=quarterly, 3=semester, 4=annual, 5=biweekly, 6=bimonthly
// Personalizada (7) não entra aqui: a cadência dela só existe no par canônico
// (frequency_type/frequency_number), então é lida do form em `recurrenceMonths`.
const recurrenceFrequencyToMonths: Record<number, number> = {
  0: 0.25, // weekly (~1 week)
  1: 1, // monthly
  2: 3, // quarterly
  3: 6, // semester
  4: 12, // annual
  5: 0.5, // biweekly (~2 weeks)
  6: 2 // bimonthly
}

const AdjustmentAccordion = <T extends FieldValues>({
  control,
  watch,
  setValue,
  trigger,
  clearErrors,
  isUnique = false,
  totalCycles = 0,
  isEdit = false,
  recurrenceFrequency = 1, // default to monthly
  currentCycle = 1,
  currentCycleDueDate,
  isProductOrLink = false
}: AdjustmentAccordionProps<T>) => {
  const hasAdjustment = watch('hasAdjustment' as Path<T>)
  const adjustmentType = watch('adjustmentSettings.adjustment_type' as Path<T>)
  const adjustmentFrequency = watch(
    'adjustmentSettings.frequency' as Path<T>
  ) as string
  const dueDate = watch('dueDate' as Path<T>) as Date
  const recurrenceFrequencyType = watch('frequency_type' as Path<T>) as
    | FrequencyUnit
    | undefined
  const recurrenceFrequencyNumber = watch('frequency_number' as Path<T>) as
    | number
    | undefined

  // Meses cobertos por UM ciclo da recorrência. Todo cálculo de data/ciclo do reajuste
  // usa isso como passo. Personalizada (enum 7) não está no mapa de presets — sem o par
  // canônico, uma recorrência de 2 anos cairia no fallback mensal e o próximo reajuste
  // seria agendado 12 meses cedo demais.
  const recurrenceMonths = useMemo(() => {
    const preset = recurrenceFrequencyToMonths[recurrenceFrequency]
    if (preset) return preset

    if (recurrenceFrequencyType && Number(recurrenceFrequencyNumber) > 0) {
      return monthsPerCycle(
        recurrenceFrequencyType,
        Number(recurrenceFrequencyNumber)
      )
    }

    return 1
  }, [recurrenceFrequency, recurrenceFrequencyType, recurrenceFrequencyNumber])
  // Composição atual do formulário — é dela que sai a lista de itens selecionáveis,
  // sem precisar de prop nova.
  const composedItems = (watch('items' as Path<T>) || []) as any[]

  const getItemLabel = (item: any, index: number) =>
    item?.description || item?.product?.label || `Item ${index + 1}`

  // Na EDIÇÃO os itens já têm uuid de servidor. Na CRIAÇÃO ainda não — o
  // UuidIdentifiable sobrescreve o uuid no before_create —, então a identidade é a
  // POSIÇÃO e o payload builder a converte em billing_item_indexes.
  const getItemValue = (item: any, index: number) =>
    isEdit ? item?.uuid || `item-${index}` : String(index)

  /**
   * Item que ainda não existe no banco. Só acontece na EDIÇÃO, quando o merchant
   * acrescenta uma linha à composição: o form o cria com `uuid: ''`.
   *
   * Ele fica fora da tabela do reajuste porque não há como referenciá-lo neste request.
   * `PATCH /v3/recurrences/:uuid` resolve o escopo por `billing_item_uuids` e não aceita
   * seleção posicional (o `billing_item_indexes` existe só na criação, onde o backend
   * traduz posição -> uuid depois do save). Mandar o rótulo `item-N` daria 400
   * "Item(ns) não pertencente(s) a esta recorrência", e omiti-lo em silêncio faria o
   * merchant pensar que configurou algo que não foi salvo. Aqui a limitação é explícita:
   * o item entra no reajuste na próxima edição, já com uuid.
   */
  const isUnsavedItem = (item: any) => isEdit && !item?.uuid

  const selectableItems = composedItems.filter((item) => !isUnsavedItem(item))
  const unsavedItemsCount = composedItems.length - selectableItems.length

  const allItemValues = composedItems
    .map((item, index) =>
      isUnsavedItem(item) ? null : getItemValue(item, index)
    )
    .filter((value): value is string => value !== null)
  const rawItemSelection = watch(
    'adjustmentSettings.billing_item_uuids' as Path<T>
  ) as string[] | null | undefined

  // null significa "todos os itens" — inclusive os que forem adicionados depois.
  // Na tela isso é indistinguível de tudo marcado, então a lista só é materializada
  // quando o merchant efetivamente desmarca algo.
  //
  // A seleção é intersectada com a composição atual: ao remover um item, a entrada
  // dele ficava pendurada aqui e a contagem voltava a bater com o total, fazendo o
  // payload cair no atalho "todos os itens" e reajustar justamente o item que o
  // merchant tinha desmarcado.
  const selectedItemValues = useMemo(() => {
    if (!rawItemSelection) return allItemValues
    return rawItemSelection.filter((value) => allItemValues.includes(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawItemSelection, JSON.stringify(allItemValues)])

  // Poda os órfãos também no form: sem isso o valor podado acima seria recalculado a
  // cada render, mas o payload continuaria sendo montado a partir da lista suja.
  useEffect(() => {
    if (!rawItemSelection) return

    const pruned = rawItemSelection.filter((value) =>
      allItemValues.includes(value)
    )
    if (pruned.length === rawItemSelection.length) return

    setValue(
      'adjustmentSettings.billing_item_uuids' as any,
      (pruned.length === allItemValues.length ? null : pruned) as any
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allItemValues), JSON.stringify(rawItemSelection)])

  const setSelectedItemValues: Dispatch<SetStateAction<SelectRowTable>> = (
    updater
  ) => {
    const previous: SelectRowTable = {
      useSelect: true,
      selected: selectedItemValues
    }
    const next = typeof updater === 'function' ? updater(previous) : updater

    setValue(
      'adjustmentSettings.billing_item_uuids' as any,
      // Tudo marcado volta a ser null, para não congelar o escopo e deixar item
      // futuro de fora sem o merchant ter pedido isso.
      (next.selected.length === allItemValues.length
        ? null
        : next.selected) as any,
      { shouldValidate: true }
    )
  }
  const nextAdjustmentDate = watch(
    'adjustmentSettings.next_adjustment_date' as Path<T>
  ) as string

  // Local state for the cycle input to enable bidirectional sync
  const [nextAdjustmentCycle, setNextAdjustmentCycle] = useState<string>('')
  // Ref to track which field triggered the change (to prevent infinite loops)
  const updateSourceRef = useRef<'date' | 'cycle' | null>(null)
  // Ref to track if this is the initial mount (to prevent recalculating existing dates in edit mode)
  const isInitialMountRef = useRef(true)
  // Ref to store the previous frequency to detect user-initiated changes
  const previousFrequencyRef = useRef<string | null>(null)

  // Filter adjustment frequency options based on recurrence frequency
  // Don't show frequencies smaller than the recurrence frequency
  const filteredFrequencyOptions = useMemo(() => {
    return adjustmentFrequencyOptions.filter(
      (option) => option.months >= recurrenceMonths
    )
  }, [recurrenceMonths])

  // Default frequency is always annual
  const defaultFrequency = 'annual'

  // Calculate next adjustment date based on mode (create vs edit) and frequency
  const calculateNextAdjustmentDate = useCallback(
    (frequency: string): string => {
      const frequencyOption = adjustmentFrequencyOptions.find(
        (opt) => opt.value === frequency
      )
      const adjustmentMonths = frequencyOption?.months || 12

      if (!isEdit) {
        // Creation mode: dueDate + adjustment frequency months
        const baseDate = dueDate ? moment(dueDate) : moment()
        return baseDate.add(adjustmentMonths, 'months').format('YYYY-MM-DD')
      } else {
        // Edit mode: calculate based on current cycle
        // Convert current cycle to months elapsed since start
        const monthsElapsed = (currentCycle - 1) * recurrenceMonths

        // Find next adjustment cycle
        // Next adjustment = ceil(monthsElapsed / adjustmentMonths) * adjustmentMonths
        const nextAdjustmentMonth =
          Math.ceil(monthsElapsed / adjustmentMonths) * adjustmentMonths

        // If monthsElapsed is exactly at adjustment point, go to next one
        const finalAdjustmentMonth =
          nextAdjustmentMonth <= monthsElapsed
            ? nextAdjustmentMonth + adjustmentMonths
            : nextAdjustmentMonth

        // Months to add from current cycle
        const monthsToAdd = finalAdjustmentMonth - monthsElapsed

        // Use currentCycleDueDate as base, or today if not available
        const baseDate = currentCycleDueDate
          ? moment(currentCycleDueDate)
          : moment()

        return baseDate.add(monthsToAdd, 'months').format('YYYY-MM-DD')
      }
    },
    [isEdit, dueDate, recurrenceMonths, currentCycle, currentCycleDueDate]
  )

  // Calculate cycle number from a given date
  // Returns the cycle number when the adjustment will be applied
  const calculateCycleFromDate = useCallback(
    (dateString: string): number => {
      if (!dateString) return 1

      const targetDate = moment(dateString)

      if (!isEdit) {
        // Creation mode: calculate months difference from dueDate
        // Cycle 1 is at dueDate, cycle 2 is at dueDate + recurrenceMonths, etc.
        const baseDate = dueDate ? moment(dueDate) : moment()
        const monthsDiff = targetDate.diff(baseDate, 'months', true)
        // Convert months to cycles: monthsDiff / recurrenceMonths gives us how many cycles after cycle 1
        // We add 1 because cycle 1 is at month 0
        const cycleNumber = Math.round(monthsDiff / recurrenceMonths) + 1
        return Math.max(1, cycleNumber)
      } else {
        // Edit mode: calculate from current cycle and its due date
        // Example: currentCycle=1, currentCycleDueDate=30/01/2026, targetDate=16/02/2027
        // For monthly recurrence: monthsDiff ≈ 12.5, cyclesFromNow = 13, result = 1 + 13 = 14
        const baseDate = currentCycleDueDate
          ? moment(currentCycleDueDate)
          : moment()
        const monthsDiff = targetDate.diff(baseDate, 'months', true)
        // Calculate how many cycles from the current cycle's due date to the target date
        const cyclesFromNow = Math.round(monthsDiff / recurrenceMonths)
        // The cycle number is currentCycle + cyclesFromNow
        return Math.max(1, currentCycle + cyclesFromNow)
      }
    },
    [isEdit, dueDate, recurrenceMonths, currentCycle, currentCycleDueDate]
  )

  // Calculate date from a given cycle number
  const calculateDateFromCycle = useCallback(
    (cycleNumber: number): string => {
      if (cycleNumber < 1) return moment().format('YYYY-MM-DD')

      if (!isEdit) {
        // Creation mode: dueDate + (cycle - 1) * recurrenceMonths
        const baseDate = dueDate ? moment(dueDate) : moment()
        const monthsToAdd = (cycleNumber - 1) * recurrenceMonths
        return baseDate.add(monthsToAdd, 'months').format('YYYY-MM-DD')
      } else {
        // Edit mode: currentCycleDueDate + (targetCycle - currentCycle) * recurrenceMonths
        const baseDate = currentCycleDueDate
          ? moment(currentCycleDueDate)
          : moment()
        const cycleDiff = cycleNumber - currentCycle
        const monthsToAdd = cycleDiff * recurrenceMonths
        return baseDate.add(monthsToAdd, 'months').format('YYYY-MM-DD')
      }
    },
    [isEdit, dueDate, recurrenceMonths, currentCycle, currentCycleDueDate]
  )

  // Handle date change - calculate and update cycle
  const handleDateChange = useCallback(
    (date: Date | null, fieldOnChange: (value: any) => void) => {
      const formattedDate = date ? moment(date).format('YYYY-MM-DD') : null
      fieldOnChange(formattedDate)

      if (formattedDate && updateSourceRef.current !== 'cycle') {
        updateSourceRef.current = 'date'
        const cycle = calculateCycleFromDate(formattedDate)
        setNextAdjustmentCycle(cycle.toString())
        // Reset source after a short delay
        setTimeout(() => {
          updateSourceRef.current = null
        }, 100)
      }
    },
    [calculateCycleFromDate]
  )

  // Handle cycle change - calculate and update date
  const handleCycleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      // Only allow positive integers
      if (value === '' || /^\d+$/.test(value)) {
        setNextAdjustmentCycle(value)

        if (value && updateSourceRef.current !== 'date') {
          const cycleNumber = parseInt(value, 10)
          if (cycleNumber >= 1) {
            updateSourceRef.current = 'cycle'
            const newDate = calculateDateFromCycle(cycleNumber)
            setValue(
              'adjustmentSettings.next_adjustment_date' as any,
              newDate as any
            )
            // Reset source after a short delay
            setTimeout(() => {
              updateSourceRef.current = null
            }, 100)
          }
        }
      }
    },
    [calculateDateFromCycle, setValue]
  )

  // Effect to sync cycle when date changes from external sources (frequency change, etc.)
  useEffect(() => {
    if (
      hasAdjustment &&
      nextAdjustmentDate &&
      updateSourceRef.current !== 'cycle'
    ) {
      const cycle = calculateCycleFromDate(nextAdjustmentDate)
      setNextAdjustmentCycle(cycle.toString())
    }
  }, [nextAdjustmentDate, hasAdjustment, calculateCycleFromDate])

  // A data segue obrigatória no payload (coluna NOT NULL) mesmo escondida; sem esta rede,
  // um molde sem data trava o submit num erro de campo invisível.
  useEffect(() => {
    if (!isProductOrLink || !hasAdjustment) return
    if (nextAdjustmentDate) return

    setValue(
      'adjustmentSettings.next_adjustment_date' as any,
      calculateNextAdjustmentDate(
        adjustmentFrequency || defaultFrequency
      ) as any
    )
  }, [
    isProductOrLink,
    hasAdjustment,
    nextAdjustmentDate,
    adjustmentFrequency,
    calculateNextAdjustmentDate,
    setValue
  ])

  // Check if adjustment should be enabled
  // Only enabled for recurrences with more than 1 cycle
  const isAdjustmentEnabled =
    !isUnique && (totalCycles === 0 || totalCycles > 1)

  const handleAccordionChange = (
    _: React.ChangeEvent<object>,
    isExpanded: boolean
  ) => {
    if (!isAdjustmentEnabled) {
      return
    }

    setValue('hasAdjustment' as any, isExpanded as any, {
      shouldValidate: false
    })

    if (isExpanded) {
      const currentSettings = watch('adjustmentSettings' as Path<T>)

      if (!currentSettings || !currentSettings.adjustment_type) {
        // Set default values when opening
        const initialFrequency = defaultFrequency
        const initialDate = calculateNextAdjustmentDate(initialFrequency)

        setValue(
          'adjustmentSettings' as any,
          {
            adjustment_type: '',
            frequency: initialFrequency,
            percentage_value: '',
            next_adjustment_date: initialDate,
            automatic_application: true,
            // null = todos os itens. Além de ser o comportamento histórico, é o que
            // faz item adicionado depois participar do reajuste sozinho.
            billing_item_uuids: null
          } as any,
          { shouldValidate: false }
        )
      }
    } else {
      // Clear errors when closing
      clearErrors('adjustmentSettings.percentage_value' as any)
      clearErrors('adjustmentSettings.adjustment_type' as any)
      clearErrors('adjustmentSettings.frequency' as any)
      clearErrors('adjustmentSettings.next_adjustment_date' as any)
      clearErrors('adjustmentSettings' as any)

      setTimeout(() => {
        trigger('adjustmentSettings' as any)
      }, 0)
    }
  }

  // Effect to auto-close when switching to unique charge
  useEffect(() => {
    if (!isAdjustmentEnabled && hasAdjustment) {
      setValue('hasAdjustment' as any, false as any)
    }
  }, [isAdjustmentEnabled, hasAdjustment, setValue])

  // Effect to recalculate next_adjustment_date when frequency changes
  // Only recalculate if the frequency was actually changed by the user, not on initial mount
  useEffect(() => {
    if (hasAdjustment && adjustmentFrequency) {
      // Skip recalculation on initial mount in edit mode if date already exists
      if (isInitialMountRef.current && isEdit && nextAdjustmentDate) {
        previousFrequencyRef.current = adjustmentFrequency
        isInitialMountRef.current = false
        return
      }

      // Mark as no longer initial mount
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false
      }

      // Only recalculate if frequency actually changed (user action)
      if (previousFrequencyRef.current !== adjustmentFrequency) {
        previousFrequencyRef.current = adjustmentFrequency
        const newDate = calculateNextAdjustmentDate(adjustmentFrequency)
        setValue(
          'adjustmentSettings.next_adjustment_date' as any,
          newDate as any
        )
      }
    }
  }, [
    adjustmentFrequency,
    hasAdjustment,
    calculateNextAdjustmentDate,
    setValue,
    isEdit,
    nextAdjustmentDate
  ])

  // Effect to recalculate when dueDate changes (for creation mode)
  useEffect(() => {
    if (!isEdit && hasAdjustment && dueDate && adjustmentFrequency) {
      const newDate = calculateNextAdjustmentDate(adjustmentFrequency)
      setValue('adjustmentSettings.next_adjustment_date' as any, newDate as any)
    }
  }, [
    dueDate,
    isEdit,
    hasAdjustment,
    adjustmentFrequency,
    calculateNextAdjustmentDate,
    setValue
  ])

  // Effect to ensure selected frequency is valid when recurrence frequency changes
  useEffect(() => {
    if (hasAdjustment && adjustmentFrequency) {
      const isValidFrequency = filteredFrequencyOptions.some(
        (opt) => opt.value === adjustmentFrequency
      )
      if (!isValidFrequency) {
        // Reset to first available option
        setValue('adjustmentSettings.frequency' as any, defaultFrequency as any)
      }
    }
  }, [
    filteredFrequencyOptions,
    adjustmentFrequency,
    hasAdjustment,
    defaultFrequency,
    setValue
  ])

  // Check if percentage field should be shown (only for customized type)
  const showPercentageField = adjustmentType === 'customized'

  if (!isAdjustmentEnabled) return null

  return (
    <Tooltip
      enterTouchDelay={0}
      arrow
      placement="top"
      title={
        !isAdjustmentEnabled
          ? 'O reajuste está disponível somente em recorrências com mais de 1 ciclo'
          : ''
      }
      disableHoverListener={Boolean(hasAdjustment) || isAdjustmentEnabled}
      PopperProps={{
        sx: {
          pointerEvents: hasAdjustment ? 'none' : 'auto'
        }
      }}
    >
      <Accordion
        expanded={Boolean(hasAdjustment)}
        onChange={handleAccordionChange}
        disabled={!isAdjustmentEnabled}
        sx={{
          border: '1px solid #E8EBE8',
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          borderRadius: '8px !important',
          backgroundColor: '#FAFBFA',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            backgroundColor: '#fff'
          },
          '&.Mui-disabled': {
            backgroundColor: '#FAFBFA !important'
          },
          position: 'relative'
        }}
      >
        <AccordionSummary
          sx={{ '.MuiAccordionSummary-content': { margin: '12px 0px' } }}
          hasSwitch
          expandIcon={
            <Switch
              disabled={!isAdjustmentEnabled}
              checked={Boolean(hasAdjustment)}
            />
          }
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="smBold">Reajuste</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap={2}>
            {/* Adjustment Type */}
            <Controller
              control={control}
              name={'adjustmentSettings.adjustment_type' as Path<T>}
              render={({ field, fieldState: { error } }) => (
                <Select
                  id="adjustment-type-select"
                  label="Tipo de reajuste"
                  placeholder="Selecione o tipo de reajuste"
                  helperText={error?.message}
                  error={!!error?.message}
                  fullWidth
                  value={field.value || ''}
                  onChange={(event) => {
                    field.onChange(event)
                    // Clear percentage value when changing from customized
                    if (event.target.value !== 'customized') {
                      setValue(
                        'adjustmentSettings.percentage_value' as any,
                        '' as any
                      )
                    }
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      color: !field.value
                        ? theme.palette.neutral[400]
                        : 'inherit'
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    Selecione o tipo
                  </MenuItem>
                  {adjustmentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            {/* Percentage Value - Only for customized type */}
            {showPercentageField && (
              <Controller
                control={control}
                name={'adjustmentSettings.percentage_value' as Path<T>}
                render={({ field, fieldState: { error } }) => (
                  <Textfield
                    id="adjustment-percentage-input"
                    label="Percentual de reajuste"
                    placeholder="Ex: 10"
                    helperText={error?.message}
                    error={!!error?.message}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                      style: { fontSize: '14px' }
                    }}
                    endAdornment={true}
                    customEndAdornment="%"
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e)
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '14px'
                      }
                    }}
                  />
                )}
              />
            )}

            {/* Frequency */}
            <Controller
              control={control}
              name={'adjustmentSettings.frequency' as Path<T>}
              render={({ field, fieldState: { error } }) => (
                <Select
                  id="adjustment-frequency-select"
                  label="Frequência do reajuste"
                  placeholder="Selecione a frequência"
                  helperText={error?.message}
                  error={!!error?.message}
                  fullWidth
                  value={field.value || defaultFrequency}
                  onChange={field.onChange}
                >
                  {filteredFrequencyOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            {/* Next Adjustment Date and Cycle - Bidirectionally linked.
                Escondidos no molde: a data só nasce na contratação. */}
            {!isProductOrLink && (
              <Box
                display="flex"
                gap={2}
                sx={{
                  '& > *': { flex: 1 }
                }}
              >
                <Controller
                  control={control}
                  name={'adjustmentSettings.next_adjustment_date' as Path<T>}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Data do próximo reajuste"
                      value={field.value ? moment(field.value).toDate() : null}
                      onChange={(date: Date | null) => {
                        handleDateChange(date, field.onChange)
                      }}
                      error={!!error?.message}
                      helperText={error?.message}
                      minDate={new Date()}
                      renderInput={(props: any) => (
                        <Textfield {...props} name="next_adjustment_date" />
                      )}
                    />
                  )}
                />
                <Box>
                  <Textfield
                    id="adjustment-cycle-input"
                    label="Ciclo do reajuste"
                    placeholder="Ex: 12"
                    value={nextAdjustmentCycle}
                    onChange={handleCycleChange}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      style: { fontSize: '14px' }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '14px'
                      }
                    }}
                  />
                  <Typography
                    variant="xsLight"
                    sx={{
                      color: theme.palette.neutral[400],
                      mt: '4px',
                      display: 'block'
                    }}
                  >
                    Ciclo em que o reajuste será aplicado
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Itens do reajuste — todos marcados por padrão, o merchant desmarca
                o que não quer. Aparece mesmo com um item só: o fluxo ser sempre o
                mesmo vale mais do que economizar a linha. */}
            {composedItems.length > 0 && (
              <Box>
                <Typography
                  variant="smLight"
                  sx={{ color: theme.palette.neutral[400], mb: '6px' }}
                >
                  Itens do reajuste
                </Typography>
                <Controller
                  control={control}
                  name={'adjustmentSettings.billing_item_uuids' as Path<T>}
                  render={({ fieldState: { error } }) => (
                    <>
                      {/* Composição só com item novo: a tabela não teria linha nenhuma,
                          e uma tabela vazia sugeriria que não há o que selecionar. */}
                      {selectableItems.length > 0 && (
                        <Table
                          header={[
                            { id: 'item', label: 'Item' },
                            { id: 'amount', label: 'Valor' }
                          ]}
                          rows={composedItems
                            .map((item, index) => ({ item, index }))
                            .filter(({ item }) => !isUnsavedItem(item))
                            .map(({ item, index }) => ({
                              uuid: getItemValue(item, index),
                              item: getItemLabel(item, index),
                              amount: formatMoney(Number(item?.total || 0))
                            }))}
                          select={{
                            useSelect: true,
                            selected: selectedItemValues,
                            setSelected: setSelectedItemValues
                          }}
                          hoverInRow
                          hiddePagination
                          disableFilter
                        />
                      )}
                      {/* Item recém-adicionado não aparece na tabela acima (ver
                          isUnsavedItem). Dizer isso é obrigatório: sem o aviso, o
                          merchant só descobriria no ciclo seguinte que o item novo
                          ficou fora do reajuste. */}
                      {unsavedItemsCount > 0 && (
                        <Typography
                          variant="xsLight"
                          sx={{
                            color: theme.palette.neutral[400],
                            mt: '6px',
                            display: 'block'
                          }}
                        >
                          {unsavedItemsCount === 1
                            ? 'O item adicionado agora entra no reajuste depois que você salvar a recorrência.'
                            : `Os ${unsavedItemsCount} itens adicionados agora entram no reajuste depois que você salvar a recorrência.`}
                        </Typography>
                      )}

                      {/* O schema já barra a submissão; isto é para o erro aparecer
                          onde o merchant errou, e não só no toast. */}
                      {!!error?.message && (
                        <Typography
                          variant="xsLight"
                          sx={{
                            color: theme.palette.error?.main || '#D32F2F',
                            mt: '6px',
                            display: 'block'
                          }}
                        >
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            )}

            {/* Automatic Application */}
            <Box display="flex" alignItems="center" gap={1}>
              <Controller
                control={control}
                name={'adjustmentSettings.automatic_application' as Path<T>}
                render={({ field }) => (
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="smLight">
                  Reajustar automaticamente
                </Typography>
                <Tooltip
                  enterTouchDelay={0}
                  arrow
                  placement="top"
                  title="Quando desativado, o valor da recorrência deverá ser confirmado manualmente na aba “Próximos reajustes”"
                >
                  <Box>
                    <Icon
                      icon="info"
                      sx={{
                        fontSize: '1rem',
                        mt: '6px',
                        color: theme.palette.neutral[600]
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Tooltip>
  )
}

export default AdjustmentAccordion
